'use client';

import {
  LoginWithRedirectOptions,
  PromptOptions,
  useCommonAuthProvider,
} from '@modules/auth/providers/CommonAuthProvider/CommonAuthProvider';
import { useFlagsProxy } from '@modules/feature-flags/hooks/useFlagsProxy';
import { CustomSpin } from '@modules/shared/components/CustomSpin';
import { setCookie } from '@utils/CookieUtil';
import { COOKIE_NAMES } from '@utils/constants';
import { getUTMs } from '@utils/utm';

import { Route } from 'next';
import { useRouter } from 'next/navigation';
import React, { FC, useEffect } from 'react';

function buildLoginParamsFromOptions(
  options: LoginWithRedirectOptions,
  utms: Record<string, string>,
  cloudMspEnableUiFeatures: boolean,
): LoginWithRedirectOptions {
  return {
    redirect_uri: options?.redirect_uri,
    returnTo: options?.returnTo,
    prompt: options?.prompt as PromptOptions,
    connection: options?.connection,
    organizationId: options?.organizationId,
    customQueryParams: options?.customQueryParams,
    utm: utms,
    ...(cloudMspEnableUiFeatures
      ? {
        screen_hint: options?.screen_hint,
        loginHint: options?.loginHint,
      }
      : {}
    ),
  };
}

export const Auth0: FC<LoginWithRedirectOptions> = (options) => {
  const router = useRouter();
  const { login, loginWithRedirect } = useCommonAuthProvider();
  const { serviceAuth0Available, cloudMspEnableUiFeatures, cloudAuthNewLoginExperience } = useFlagsProxy();

  useEffect(() => {
    if (serviceAuth0Available === undefined) {
      return;
    }

    if (!serviceAuth0Available) {
      router.push('/login-unavailable' as Route);

      return;
    }

    const params = new URLSearchParams(window?.location?.search);
    const criblDocs = params.get('criblDocs');

    if (criblDocs) {
      setCookie(COOKIE_NAMES.CRIBL_DOCS, criblDocs);
    }

    const utms = getUTMs();
    const loginParams = buildLoginParamsFromOptions(options, utms, cloudMspEnableUiFeatures);

    if (cloudAuthNewLoginExperience && !options.organizationId) {
      void login(loginParams);

      return;
    }

    loginWithRedirect(loginParams);
  }, [
    serviceAuth0Available,
    options,
    cloudAuthNewLoginExperience,
    cloudMspEnableUiFeatures,
    login,
    loginWithRedirect,
    router,
  ]);

  return <CustomSpin />;
};
